import React, { useCallback } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { ContentWithSections, SectionFormationGrid, FormationItem } from '../../types/pageTypes';
import { Volume2 } from 'lucide-react';
import { speakTamil } from '../../utils/tts';

interface PhonicsSyllableFormV2Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

function getSections(page: PageDetailOut) {
  const content = page.page_json_v1?.content as ContentWithSections | undefined;
  return content?.sections ?? [];
}

export const PhonicsSyllableFormV2: React.FC<PhonicsSyllableFormV2Props> = ({ page }) => {
  const sections = getSections(page);
  const heading = sections.find((s) => s.type === 'heading');
  const note = sections.find((s) => s.type === 'note');
  const formation = sections.find((s) => s.type === 'formation_grid') as SectionFormationGrid | undefined;
  const items = formation?.items ?? formation?.cards ?? [];

  const playResult = useCallback((item: FormationItem) => {
    const t = item.result ?? [item.lhs, item.vowel].filter(Boolean).join('');
    if (t) speakTamil(t);
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
        {page.page_title || (heading as { text?: string })?.text || 'Syllable Formation'}
      </h1>

      {(heading as { text?: string })?.text && (
        <h2 className="text-xl font-medium text-gray-800 dark:text-gray-200">
          {(heading as { text?: string }).text}
        </h2>
      )}

      {items.length > 0 && (
        <div className="space-y-3">
          {formation?.title && (
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{formation.title}</h3>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {items.map((card, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-center gap-2 text-2xl font-medium text-gray-600 dark:text-gray-400 mb-2">
                  <span>{card.lhs}</span>
                  <span>{card.op ?? '+'}</span>
                  <span>{card.vowel}</span>
                  <span>=</span>
                </div>
                <button
                  type="button"
                  onClick={() => playResult(card)}
                  className="w-full py-4 text-4xl font-bold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {card.result ?? '—'}
                  <Volume2 className="w-8 h-8" />
                </button>
                {Array.isArray(card.examples) && card.examples.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Examples</p>
                    <p className="text-lg text-gray-700 dark:text-gray-300">
                      {card.examples.map((ex) => ex.word).filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(note as { text?: string })?.text && (
        <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
          <p className="text-gray-700 dark:text-gray-300">{(note as { text?: string }).text}</p>
        </div>
      )}

    </div>
  );
};
